import React, { useCallback, useRef, useState, FC } from 'react'
import { Box, TertiaryButton } from '@moonpig/launchpad-components'
import { formatPrice } from '@moonpig/web-core-utils'
import { trackGAEvent } from '@moonpig/web-core-analytics'
import { Region } from '@moonpig/web-core-types'
import { useLanguage, useStoreId } from '@moonpig/web-core-stores'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import { CardProductTile } from './CardProductTile'
import {
  ProductItemEventEnum,
  ProductList,
  ProductClickedEvent,
  AddedToBasketEvent,
} from '../../types'
import { InternalUserPanel } from './InternalUserPanel'
import { ProductTileProduct } from './ProductTileProduct'
import { useLocaleText } from './ProductTile.locale'
import { getProductImageDimensions } from '../../utils/getProductImageDimensions'
import { isCardProduct } from '../../utils/isCardProduct'
import { isPortraitCard } from '../../utils/isPortraitCard'
import { createProductItemGAEvent } from '../../analytics'
import { transformPill } from '../../utils/transformPill'
import { useProductModal } from '../../contexts/productModal/Context'
import { CardProductTileVariant } from './CardProductTileVariant/CardProductTileVariant'
import { CardProductTileGalleryVariant } from './CardProductTileGalleryVariant'
import { getCardFormat } from '../../utils/getCardFormat/getCardFormat'
import { hasMultipleVariants } from '../../utils/hasMultipleVariants'
import { CtaClickProps, ProductTileCta } from './ProductTileCta'
import { NonCardProductTileWithCarousel } from './NonCardProductTileWithCarousel'
import { NonCardProductTile } from './NonCardProductTile'
import { GiftTileVariant } from './GiftTileVariant/GiftTileVariant'
import { isProductPrintedInCard } from '../../utils'
import { useProductInitialVariantSelectionState } from '../../utils/useProductInitialVariantSelectionState'

function useCallbackOnce<T>(callback: (arg: T) => void) {
  const [firstInvocation, setFirstInvocation] = useState(true)

  return React.useCallback(
    (arg: T) => {
      if (firstInvocation) {
        callback(arg)
        setFirstInvocation(false)
      }
    },
    [callback, firstInvocation, setFirstInvocation],
  )
}

enum ProductTileCtaActionEnum {
  ADD_TO_BASKET = 'add-to-basket',
  SHOW_MODAL = 'show-modal',
  REDIRECT_TO_PDP = 'redirect-to-pdp',
}

type ReactMouseEvent = React.MouseEvent<HTMLAnchorElement, MouseEvent>

type Props = {
  product: ProductTileProduct
  trackingData: {
    pageType: string
    metaTitle?: string
    region?: Region
    tile: {
      productIndex: number
      totalNumberOfProducts: number
    }
    productList?: ProductList
  }
  ref?: React.MutableRefObject<null>
  onClick: (clickedEvent: ProductClickedEvent) => void
  onFirstClick?: (clickedEvent: ProductClickedEvent) => void
  onAddToBasket: (addedEvent: AddedToBasketEvent) => void
  isInternalUser?: boolean
  isFavourited: boolean
  handleFavourite: (
    product: ProductTileProduct,
    isFavouriteSelected: boolean,
    index: number,
  ) => Promise<{ removeWithConfirmation: boolean }>
  isGallery?: boolean | undefined
  showOurPick?: boolean
  showCta?: boolean
  tabbedCarousel?: boolean
  showCardProductTileVariant?: boolean
  onTrackingInfo?: (e: { product: ProductTileProduct }) => {
    label: string
    listName: string
    itemIndex: number
  }
  multiBuyEnabled?: boolean
}

export const ProductTile: FC<Props> = ({
  product,
  trackingData,
  onClick,
  onFirstClick,
  onAddToBasket,
  isFavourited,
  handleFavourite,
  isInternalUser = false,
  isGallery,
  showOurPick = false,
  tabbedCarousel,
  showCardProductTileVariant = false,
  onTrackingInfo,
  showCta = true,
  multiBuyEnabled = false,
}) => {
  const productTileRef = useRef<HTMLAnchorElement>(null)
  const {
    title,
    id: productId,
    masterVariant,
    rating,
    category: { slug: categorySlug },
    slug,
    publishDate,
    isLandscape,
    primaryProductPill,
    clickRankDocumentCount,
    dynamicImages,
  } = product
  const region = useStoreId()
  const productIndex = trackingData.tile.productIndex

  const { variantKey } = useProductInitialVariantSelectionState()

  const localiseText = useLocaleText()
  const language = useLanguage()
  const { show } = useProductModal()

  const isDynamicAgeEnabled = useExperiment('search-use-dynamic-images', {
    fallback: false,
  })

  const isGiftTileWithCarouselEnabled = useExperiment(
    'search-use-gift-tile-with-carousel',
    {
      fallback: false,
    },
  )

  const isGiftsFlowersModalEnabled =
    useExperiment('cvr-gifts-flowers-modal-vs-pdp', {
      fallback: 'control',
    }) === 'variant'

  const isGiftTileRedesignEnabled =
    useExperiment('search-use-gifting-tile-redesign', {
      fallback: 'control',
    }) === 'variant'

  const dynamicUrl = isDynamicAgeEnabled
    ? dynamicImages?.medium?.webp?.[0]?.url
    : undefined

  const { productImages, price, fullPrice, discountedPercentage } =
    masterVariant

  const mainImageUrl = masterVariant.productImages.mainImage.medium.jpgUrl

  const formattedPrice = formatPrice(
    price.centAmount,
    price.fractionDigits,
    price.currencyCode,
    undefined,
    language,
  )

  const formattedFullPrice = formatPrice(
    fullPrice.centAmount,
    fullPrice.fractionDigits,
    fullPrice.currencyCode,
    undefined,
    language,
  )

  const isCard = product.category && isCardProduct(product.category.department)

  const isNonCardWithMultipleVariants =
    !isCard && hasMultipleVariants(product.variants)

  const fromPrice = isNonCardWithMultipleVariants
    ? `${localiseText('non_card_product_tile.from_price')} ${formattedPrice}`
    : formattedPrice

  const productDetailsURL = `/${region}/${categorySlug}/p/${slug}/${productId.toLowerCase()}/`

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const firstClickCallback = onFirstClick && useCallbackOnce(onFirstClick)

  const getTrackingInfo = useCallback(() => {
    const { pageType, metaTitle, productList, tile } = trackingData
    const trimmedPageType = pageType.trim()
    const getLabel = (listNameLabel: string) =>
      `${listNameLabel} | ${productIndex + 1}/${tile.totalNumberOfProducts} | ${
        product.id
      }`

    if (!productList) {
      const listName = metaTitle
        ? `${trimmedPageType} | ${metaTitle}`
        : trimmedPageType
      return {
        label: getLabel(listName),
        listName,
      }
    }

    const carouselType = `${tabbedCarousel ? 'tabbed ' : ''}carousel`
    const modulesCount = `${productList.position}/${productList.totalNumberOfLists}`
    const listName = tabbedCarousel
      ? `${trimmedPageType} | ${productList.tabbedModuleTitle} | ${carouselType} | ${modulesCount} | ${productList.title} tab`
      : `${trimmedPageType} | ${productList.title} | ${carouselType} | ${modulesCount}`
    const label = getLabel(listName)
    return {
      label,
      listName,
    }
  }, [trackingData, product, productIndex, tabbedCarousel])

  const handleClick = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault()

      if (isGallery && !isCard && !isGiftsFlowersModalEnabled) {
        window.open(productDetailsURL, '_self')
      } else {
        show({
          triggerElementRef: productTileRef,
          product,
          onTrackingInfo,
        })
      }

      const trackingInfo = onTrackingInfo?.({ product }) || getTrackingInfo()

      trackGAEvent(
        createProductItemGAEvent({
          eventType: ProductItemEventEnum.SELECT_ITEM,
          product,
          index: productIndex,
          variant: product.masterVariant.title,
          ...trackingInfo,
        }),
      )

      onClick({ product, variant: masterVariant, index: productIndex })

      if (firstClickCallback) {
        firstClickCallback({
          product,
          variant: masterVariant,
          index: productIndex,
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onClick, product, masterVariant, productIndex, firstClickCallback],
  )

  const imageDimensions = getProductImageDimensions(
    isCard,
    masterVariant.key,
    isLandscape,
  )

  const getProductTileCtaAction = useCallback((): ProductTileCtaActionEnum => {
    if (isProductPrintedInCard(product.category.department)) {
      return ProductTileCtaActionEnum.REDIRECT_TO_PDP
    }
    if (
      isNonCardWithMultipleVariants &&
      isGallery &&
      !isGiftsFlowersModalEnabled
    ) {
      return ProductTileCtaActionEnum.REDIRECT_TO_PDP
    }
    if (isNonCardWithMultipleVariants) {
      return ProductTileCtaActionEnum.SHOW_MODAL
    }

    return ProductTileCtaActionEnum.ADD_TO_BASKET
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const productTileCtaClick = useCallback((): CtaClickProps => {
    const ctaAction = getProductTileCtaAction()

    if (ctaAction === ProductTileCtaActionEnum.REDIRECT_TO_PDP) {
      return {
        href: productDetailsURL,
      }
    }

    if (ctaAction === ProductTileCtaActionEnum.SHOW_MODAL) {
      return {
        onClick: async e => {
          e.preventDefault()
          show({
            triggerElementRef: productTileRef,
            product,
          })
        },
      }
    }

    return {
      onClick: async e => {
        e.preventDefault()
        onAddToBasket({
          product,
          index: productIndex || 0,
          productIndex: productIndex || 0,
          variant: product.masterVariant,
          quantity: 1,
          trackPersonaliseEvent: true,
        })
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderLinkAs = useCallback(
    (linkProps: React.ComponentPropsWithoutRef<typeof TertiaryButton>) => (
      <ProductTileCta
        {...linkProps}
        ctaProps={productTileCtaClick()}
        department={product.category.department}
        isPrintedInCard={isPrintedInCard}
        hasMultipleVariants={hasMultipleVariants(product.variants)}
        isCustomisable={product.customisable}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const renderGiftTileLinkAs = useCallback(
    (linkProps: React.ComponentPropsWithoutRef<typeof TertiaryButton>) => (
      <ProductTileCta
        {...linkProps}
        iconOnly
        ctaProps={productTileCtaClick()}
        department={product.category.department}
        isPrintedInCard={isPrintedInCard}
        hasMultipleVariants={hasMultipleVariants(product.variants)}
        isCustomisable={product.customisable}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const label = primaryProductPill ? primaryProductPill.displayLabel : undefined
  const variant = primaryProductPill
    ? transformPill(primaryProductPill).displayVariant
    : undefined

  const frontImage =
    productImages && productImages.images.length > 0
      ? productImages.images[0].medium.jpgUrl
      : mainImageUrl

  const hasInsideRight = productImages && productImages.images.length >= 3

  const cardTileImage = dynamicUrl || mainImageUrl

  const isPrintedInCard = isProductPrintedInCard(product.category.department)

  const isMultiBuyExperimentEnabled =
    useExperiment('christmas-multi-buy', {
      fallback: 'control',
    })?.toLowerCase() === 'variant'

  const selectedVariantSku =
    product.variants.find(productVariant => productVariant.key === variantKey)
      ?.sku ??
    product.masterVariant.sku ??
    ''

  return (
    <>
      {isInternalUser && (
        <InternalUserPanel
          productId={productId}
          publishDate={(publishDate && new Date(publishDate)) || undefined}
          clickRankDocumentCount={clickRankDocumentCount || 0}
          hasDynamicImage={!!dynamicUrl}
        />
      )}
      {isCard &&
        isGallery &&
        (showCardProductTileVariant ? (
          <CardProductTileVariant
            images={{
              front: {
                src: frontImage,
                alt: title,
                dimensions: imageDimensions,
              },
              insideRight: hasInsideRight
                ? {
                    src: productImages.images[2].small.jpgUrl,
                    alt: title,
                    dimensions: imageDimensions,
                  }
                : undefined,
            }}
            onClick={handleClick}
            href={productDetailsURL}
            format={getCardFormat({
              isLandscape: !!isLandscape,
              variantKey: masterVariant.key,
            })}
            key={`product-${productId}`}
            pill={
              label && variant
                ? { label, variant }
                : /* istanbul ignore next */ undefined
            }
            favourite={{
              isSelected: isFavourited,
              onSelect: () =>
                handleFavourite(product, !!isFavourited, productIndex),
            }}
            isSponsored={product.isSponsored}
            ref={productTileRef}
          />
        ) : (
          <CardProductTileGalleryVariant
            href={productDetailsURL}
            image={{
              src: cardTileImage,
              alt: title,
            }}
            onClick={handleClick}
            key={`product-${productId}`}
            ref={productTileRef}
            portraitCardAspectRatio={
              imageDimensions.width / imageDimensions.height
            }
            favourite={{
              isSelected: isFavourited,
              onSelect: () =>
                handleFavourite(product, !!isFavourited, productIndex),
            }}
            label={label}
            variant={variant}
            sponsored={product.isSponsored}
            trackingData={trackingData}
            selectedVariantSku={selectedVariantSku}
            multiBuyListProductCount={product.multiBuyList?.count || 0}
            productId={productId}
            isMultiBuyEnabled={isMultiBuyExperimentEnabled && multiBuyEnabled}
          />
        ))}
      {isCard && !isGallery && (
        <Box width={1}>
          <CardProductTile
            href={productDetailsURL}
            image={{
              src: cardTileImage,
              alt: title,
            }}
            onClick={handleClick}
            key={`product-${productId}`}
            ref={productTileRef}
            portraitCardAspectRatio={
              isPortraitCard(masterVariant.key, isLandscape)
                ? imageDimensions.width / imageDimensions.height
                : /* istanbul ignore next */
                  undefined
            }
            favourite={{
              isSelected: isFavourited,
              onSelect: () =>
                handleFavourite(product, !!isFavourited, productIndex),
            }}
            ourPick={showOurPick && productIndex === 0}
            sponsored={product.isSponsored}
            label={label}
            variant={variant}
            isMultiBuyEnabled={isMultiBuyExperimentEnabled && multiBuyEnabled}
            selectedVariantSku={selectedVariantSku}
            multiBuyListProductCount={product.multiBuyList?.count}
            productId={productId}
          />
        </Box>
      )}
      {!isCard &&
        isGiftTileWithCarouselEnabled &&
        isGallery &&
        !isGiftTileRedesignEnabled && (
          <NonCardProductTileWithCarousel
            productId={productId}
            href={productDetailsURL}
            title={title}
            price={fromPrice}
            fullPrice={formattedFullPrice}
            discountedPercentage={discountedPercentage}
            image={{
              src: mainImageUrl,
              alt: title,
            }}
            images={productImages?.images}
            rating={rating || undefined}
            titleAs="h3"
            priceAs="p"
            onClick={handleClick}
            key={`product-${productId}`}
            ref={productTileRef}
            ourPick={
              showOurPick && /* istanbul ignore next */ productIndex === 0
            }
            sponsored={product.isSponsored}
            favourite={{
              isSelected: isFavourited,
              onSelect: /* istanbul ignore next */ () =>
                handleFavourite(product, !!isFavourited, productIndex),
            }}
            pill={
              /* istanbul ignore next */
              primaryProductPill ? transformPill(primaryProductPill) : undefined
            }
            linkAs={
              showCta ? renderLinkAs : /* istanbul ignore next */ undefined
            }
          />
        )}
      {((!isCard &&
        !isGiftTileWithCarouselEnabled &&
        !isGiftTileRedesignEnabled) ||
        (!isCard &&
          isGiftTileWithCarouselEnabled &&
          !isGiftTileRedesignEnabled &&
          !isGallery)) && (
        <NonCardProductTile
          productId={productId}
          href={productDetailsURL}
          title={title}
          price={fromPrice}
          fullPrice={formattedFullPrice}
          discountedPercentage={discountedPercentage}
          image={{
            src: mainImageUrl,
            alt: title,
          }}
          rating={rating || undefined}
          titleAs="h3"
          priceAs="p"
          onClick={handleClick}
          key={`product-${productId}`}
          ref={productTileRef}
          ourPick={showOurPick && productIndex === 0}
          sponsored={product.isSponsored}
          favourite={{
            isSelected: isFavourited,
            onSelect: () =>
              handleFavourite(product, !!isFavourited, productIndex),
          }}
          pill={
            primaryProductPill ? transformPill(primaryProductPill) : undefined
          }
          linkAs={showCta ? renderLinkAs : undefined}
        />
      )}
      {!isCard && isGiftTileRedesignEnabled && (
        <GiftTileVariant
          productId={productId}
          href={productDetailsURL}
          title={title}
          price={fromPrice}
          fullPrice={formattedFullPrice}
          discountedPercentage={discountedPercentage}
          image={{
            src: mainImageUrl,
            alt: title,
          }}
          images={productImages?.images}
          titleAs="h3"
          priceAs="p"
          onClick={handleClick}
          key={`product-${productId}`}
          ref={productTileRef}
          ourPick={showOurPick && productIndex === 0}
          sponsored={product.isSponsored}
          favourite={{
            isSelected: isFavourited,
            onSelect: () =>
              handleFavourite(product, !!isFavourited, productIndex),
          }}
          pill={
            primaryProductPill ? transformPill(primaryProductPill) : undefined
          }
          department={product.category.department}
          isPrintedInCard={isPrintedInCard}
          hasMultipleVariants={hasMultipleVariants(product.variants)}
          isCustomisable={product.customisable}
          showBorder={!isGallery}
          linkAs={showCta ? renderGiftTileLinkAs : undefined}
        />
      )}
    </>
  )
}

ProductTile.displayName = 'ProductTile'
